import { useEffect, useMemo, useRef } from "react";
import { useCartState, useCartMethodExecute } from "@made-people/grease-cart";
import { useGreaseGrowthbookFeatureValue } from "@made-people/grease-growthbook";

const FREE_SHIPPING_FEATURE_KEY = "free-shipping-4-tiers";

type FreeShippingCurrencyConfig = { threshold: string; voucherCode?: string };
type FreeShippingThresholdConfig = Record<
  string,
  FreeShippingCurrencyConfig | string
> & {
  voucherCode?: string;
};

function isFreeShippingCurrencyConfig(
  value: unknown,
): value is FreeShippingCurrencyConfig {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as FreeShippingCurrencyConfig).threshold === "string"
  );
}

export type GrowthbookFreeShippingCurrencyConfig = {
  threshold: string | null;
  currency: string;
  voucherCode: string;
};

export type MmFreeShippingThresholdData = {
  growthbookCurrencyConfig: GrowthbookFreeShippingCurrencyConfig | null;
  shouldUseGrowthbookThreshold: () => boolean;
  shouldAutoApplyVoucher: boolean;
  shouldAutoRemoveVoucher: boolean;
  growthbookVoucherCodesToRemove: string[];
};

export function useMmFreeShippingThresholdData(): MmFreeShippingThresholdData {
  const { cart } = useCartState();
  const freeShippingThresholdConfig =
    useGreaseGrowthbookFeatureValue<FreeShippingThresholdConfig | null>(
      FREE_SHIPPING_FEATURE_KEY,
      null,
    );

  const growthbookCurrencyConfig =
    useMemo((): GrowthbookFreeShippingCurrencyConfig | null => {
      const config = freeShippingThresholdConfig;
      if (!config || typeof config !== "object") return null;
      if (Object.keys(config).length === 0) {
        return { threshold: null, currency: "SEK", voucherCode: "NA" };
      }
      const resolvedCurrency = cart?.currencyCode;
      if (!resolvedCurrency || typeof resolvedCurrency !== "string")
        return null;

      const currencyKey = Object.keys(config).find(
        (k) => k.toUpperCase() === resolvedCurrency.toUpperCase(),
      );
      const currencyConfig = currencyKey ? config[currencyKey] : undefined;

      if (!isFreeShippingCurrencyConfig(currencyConfig)) return null;

      const voucherCode =
        (typeof currencyConfig.voucherCode === "string"
          ? currencyConfig.voucherCode
          : undefined) ??
        (typeof config.voucherCode === "string"
          ? config.voucherCode
          : undefined);

      if (!currencyConfig.threshold || !voucherCode) return null;

      return {
        threshold: String(currencyConfig.threshold),
        currency: resolvedCurrency,
        voucherCode: String(voucherCode),
      };
    }, [freeShippingThresholdConfig, cart?.currencyCode]);

  const cartTotal = useMemo(() => {
    const totals = cart?.totals;
    if (!totals?.grandTotal || totals.shipping === undefined) return 0;
    return (totals.grandTotal.number ?? 0) - (totals.shipping.number ?? 0);
  }, [cart?.totals]);

  const shouldUseGrowthbookThreshold = (): boolean =>
    Boolean(growthbookCurrencyConfig);

  const targetVoucherApplied = useMemo(() => {
    if (!growthbookCurrencyConfig || !cart?.vouchers) return false;

    return cart.vouchers.some(
      (v) =>
        v.type === "code" && v.code === growthbookCurrencyConfig.voucherCode,
    );
  }, [cart?.vouchers, growthbookCurrencyConfig]);

  const isExpressShipping = useMemo(
    () =>
      cart?.shippingMethod?.code?.toUpperCase().includes("EXPRESS") ?? false,
    [cart?.shippingMethod?.code],
  );

  const shouldAutoApplyVoucher = useMemo(() => {
    if (!growthbookCurrencyConfig?.threshold || isExpressShipping) return false;
    const threshold = parseFloat(growthbookCurrencyConfig.threshold);
    if (cartTotal < threshold) return false;
    return !targetVoucherApplied;
  }, [
    growthbookCurrencyConfig,
    cartTotal,
    targetVoucherApplied,
    isExpressShipping,
  ]);

  const shouldAutoRemoveVoucher = useMemo(() => {
    if (isExpressShipping && targetVoucherApplied) return true;
    if (!growthbookCurrencyConfig?.threshold || !targetVoucherApplied)
      return false;
    const threshold = parseFloat(growthbookCurrencyConfig.threshold);
    return cartTotal < threshold;
  }, [
    growthbookCurrencyConfig,
    targetVoucherApplied,
    cartTotal,
    isExpressShipping,
  ]);

  const growthbookVoucherCodesToRemove = useMemo(() => {
    const targetCode = growthbookCurrencyConfig?.voucherCode ?? null;
    if (!cart?.vouchers) return [];
    return cart.vouchers
      .filter(
        (v) =>
          v.type === "code" &&
          v.code !== targetCode &&
          v.name.includes("Free Shipping A/B test"),
      )
      .map((v) => (v.type === "code" ? v.code : ""))
      .filter(Boolean);
  }, [cart?.vouchers, growthbookCurrencyConfig?.voucherCode]);

  return {
    growthbookCurrencyConfig,
    shouldUseGrowthbookThreshold,
    shouldAutoApplyVoucher,
    shouldAutoRemoveVoucher,
    growthbookVoucherCodesToRemove,
  };
}

export function useMmFreeShippingThreshold() {
  const data = useMmFreeShippingThresholdData();
  return {
    growthbookCurrencyConfig: data.growthbookCurrencyConfig,
    shouldUseGrowthbookThreshold: data.shouldUseGrowthbookThreshold,
  };
}

export function useMmFreeShippingVoucherSyncEffect() {
  const {
    growthbookCurrencyConfig,
    shouldAutoApplyVoucher,
    shouldAutoRemoveVoucher,
    growthbookVoucherCodesToRemove,
  } = useMmFreeShippingThresholdData();

  const addVoucherExecute = useCartMethodExecute("addVoucher");
  const removeVoucherExecute = useCartMethodExecute("removeVoucher");
  const addVoucherRef = useRef(addVoucherExecute);
  const removeVoucherRef = useRef(removeVoucherExecute);
  addVoucherRef.current = addVoucherExecute;
  removeVoucherRef.current = removeVoucherExecute;
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (isProcessingRef.current) return;

    const run = async () => {
      isProcessingRef.current = true;
      try {
        // if (growthbookVoucherCodesToRemove.length > 0) {
        //   for (const code of growthbookVoucherCodesToRemove) {
        //     await removeVoucherRef.current({ voucherCode: code });
        //   }
        // }

        if (!growthbookCurrencyConfig) return;

        if (shouldAutoApplyVoucher) {
          await addVoucherRef.current({
            voucherCode: growthbookCurrencyConfig.voucherCode,
          });
        } else if (shouldAutoRemoveVoucher && growthbookCurrencyConfig) {
          await removeVoucherRef.current({
            voucherCode: growthbookCurrencyConfig.voucherCode,
          });
        }
      } catch (error) {
        console.error(
          "Failed to auto-apply/remove free shipping voucher:",
          error,
        );
      } finally {
        isProcessingRef.current = false;
      }
    };

    void run();
  }, [
    growthbookCurrencyConfig,
    shouldAutoApplyVoucher,
    shouldAutoRemoveVoucher,
    growthbookVoucherCodesToRemove,
  ]);
}

export const MmFreeShippingVoucherSync = () => {
  useMmFreeShippingVoucherSyncEffect();
  return null;
};
